/**
 * Contenu ANGLAIS des trajets de transfert, indexé par slug FR.
 *
 * Séparé de routes.ts pour respecter la limite de 400 lignes/fichier et isoler
 * la traduction du contenu structurel (slug, ids, distance, prix). Les données
 * structurelles + prix restent uniques (routes.ts + infos.ts). Voir
 * localizedRouteContent() dans index.ts.
 */
import type { TransferRouteContent } from "./routes";

/** EN content keyed by the FR slug (stable join key). */
export const TRANSFER_ROUTES_EN: Record<string, TransferRouteContent> = {
  "cannes-aeroport-nice": {
    lead: "Private transfer between Cannes and Nice Côte d'Azur airport, with a chauffeur, flight tracking and meet-and-greet at the gate. Flat rate set in advance, no surprises.",
    highlights: [
      "Real-time flight tracking: the chauffeur adjusts the pick-up time if your flight is delayed.",
      "Name-board welcome at terminal 1 or 2 depending on your airline.",
      "Direct route via the A8, around 30 minutes outside peak hours.",
      "Ideal for the Cannes Festival, MIPIM, MIPCOM and hotel arrivals on the Croisette.",
    ],
    faq: [
      {
        q: "How much is a Cannes – Nice airport transfer?",
        a: "The rate is a flat fee set in advance per vehicle category. You see the exact price per class in the table above, with no hidden costs.",
      },
      {
        q: "Does the chauffeur wait if my flight is late?",
        a: "Yes. We track your flight number and adjust the pick-up time. A 30-minute courtesy wait is included on airport arrivals.",
      },
      {
        q: "How long does the trip take?",
        a: "Around 30 minutes outside peak hours via the A8 motorway. Allow a margin during the Cannes Festival and summer weekends.",
      },
    ],
    seo: {
      title:
        "Cannes ⇄ Nice Airport Transfer — Private Chauffeur (VTC) | KS Rent Services",
      description:
        "Private Cannes – Nice airport transfer with chauffeur, flight tracking and flat rate. About 30 min, fixed price per vehicle. Book online.",
      canonical: "/en/chauffeur/transfers/cannes-nice-airport",
    },
  },
  "monaco-aeroport-nice": {
    lead: "Private transfer between Monaco and Nice Côte d'Azur airport. Uniformed chauffeur, flight tracking and flat rate, for your business trips and jet departures.",
    highlights: [
      "Frequent link for the Principality's business clientele and residents.",
      "Coordination with the Monaco heliport available on request.",
      "About 35 minutes via the lower/middle corniche or the A8.",
      "Discretion and punctuality suited to tight schedules.",
    ],
    faq: [
      {
        q: "What is the rate for a Monaco – Nice airport transfer?",
        a: "A flat fee per vehicle category, shown above. The price is guaranteed at booking, tolls and meet-and-greet included.",
      },
      {
        q: "Do you offer the link to the Monaco heliport?",
        a: "Yes, we coordinate transfers to and from the Monaco heliport. Mention it when booking so we can align the timing.",
      },
      {
        q: "Is the service available early morning or late at night?",
        a: "Yes, subject to availability, including very early or night flights. Book ahead to secure the slot.",
      },
    ],
    seo: {
      title:
        "Monaco ⇄ Nice Airport Transfer — Private Chauffeur (VTC) | KS Rent Services",
      description:
        "Private Monaco – Nice airport transfer with chauffeur and flat rate. About 35 min, fixed price per vehicle, flight tracking. Book online.",
      canonical: "/en/chauffeur/transfers/monaco-nice-airport",
    },
  },
  "aeroport-nice-saint-tropez": {
    lead: "Long-distance transfer between Nice airport and Saint-Tropez, with a private chauffeur. High-end comfort over nearly two hours, all-inclusive flat rate.",
    highlights: [
      "About 1h30 to 2h depending on summer traffic on the A8 then the gulf road.",
      "Spacious vehicles recommended (Class V) for luggage and groups.",
      "Water, chargers and climate comfort on board for the long ride.",
      "Very busy in season: book early for June-to-September arrivals.",
    ],
    faq: [
      {
        q: "How much is a Nice airport – Saint-Tropez transfer?",
        a: "The flat fee depends on the vehicle category and is shown above. It covers the whole trip, tolls and meet-and-greet included, with no surprise mileage charge.",
      },
      {
        q: "How long is the trip to Saint-Tropez?",
        a: "Around 1h30 off-season, up to 2h30 on summer weekends and traffic peaks in the Gulf of Saint-Tropez.",
      },
      {
        q: "Which vehicle should I choose?",
        a: "For the long ride with luggage, the Class V (premium van) is the most comfortable. The Class E and S sedans suit 2 to 3 passengers travelling light.",
      },
    ],
    seo: {
      title:
        "Nice Airport ⇄ Saint-Tropez Transfer — Private Chauffeur | KS Rent Services",
      description:
        "Private Nice airport – Saint-Tropez transfer with chauffeur. About 1h30, all-inclusive flat rate per vehicle. Book your gulf transfer.",
      canonical: "/en/chauffeur/transfers/nice-airport-saint-tropez",
    },
  },
  "aeroport-nice-saint-jean-cap-ferrat": {
    lead: "Private transfer between Nice airport and Saint-Jean-Cap-Ferrat, one of the most sought-after peninsulas on the Riviera. Discreet chauffeur, polished welcome, flat rate.",
    highlights: [
      "Serves the villas and hotels of the peninsula (Grand-Hôtel du Cap-Ferrat, Royal-Riviera…).",
      "Short trip of about 30 minutes via the lower corniche.",
      "Service suited to a private clientele seeking confidentiality.",
      "Coordination with villa caretakers / concierge desks available.",
    ],
    faq: [
      {
        q: "What is the price of a Nice airport – Saint-Jean-Cap-Ferrat transfer?",
        a: "A flat fee per category, shown above, guaranteed at booking. No mileage surcharge or hidden meet-and-greet fee.",
      },
      {
        q: "Do you serve the private villas on the peninsula?",
        a: "Yes, we drop off and pick up directly at the villa or hotel address, coordinating with your concierge if needed.",
      },
      {
        q: "Is the trip quick from the airport?",
        a: "Yes, about 30 minutes via the lower corniche outside peak hours. A scenic route can be offered on request.",
      },
    ],
    seo: {
      title: "Nice Airport ⇄ Saint-Jean-Cap-Ferrat Transfer | KS Rent Services",
      description:
        "Private Nice airport – Saint-Jean-Cap-Ferrat transfer with chauffeur. About 30 min, flat rate per vehicle, villa/hotel drop-off. Book now.",
      canonical: "/en/chauffeur/transfers/nice-airport-saint-jean-cap-ferrat",
    },
  },
  "monaco-saint-tropez": {
    lead: "Long-distance transfer between Monaco and Saint-Tropez with a private chauffeur. Two prestige hubs linked in comfort, on an roughly two-hour ride at a flat rate.",
    highlights: [
      "Direct link between the Principality and the Gulf of Saint-Tropez.",
      "About 2h via the A8 then the Var coastal road.",
      "Class V recommended for comfort and luggage on the long ride.",
      "Ideal for yachting, events and combined Monaco / Saint-Tropez stays.",
    ],
    faq: [
      {
        q: "How much is a Monaco – Saint-Tropez transfer?",
        a: "The flat fee, shown above per vehicle, covers the entire trip, tolls and meet-and-greet included. The price is locked at booking.",
      },
      {
        q: "How long is the Monaco – Saint-Tropez trip?",
        a: "About 2h off-season, more during summer peaks. We adjust the departure time to smooth the arrival into the gulf.",
      },
      {
        q: "Can we stop along the way?",
        a: "Yes, a stop (lunch, viewpoint, hotel) is possible. Mention it at booking so we can build it into the transfer.",
      },
    ],
    seo: {
      title:
        "Monaco ⇄ Saint-Tropez Transfer — Private Chauffeur (VTC) | KS Rent Services",
      description:
        "Private Monaco – Saint-Tropez transfer with chauffeur. About 2h, all-inclusive flat rate per vehicle. Prestige Riviera link. Book online.",
      canonical: "/en/chauffeur/transfers/monaco-saint-tropez",
    },
  },
  "cannes-monaco": {
    lead: "Private transfer between Cannes and Monaco with a chauffeur. A flagship Riviera link, ideal for events, business and nights out in the Principality.",
    highlights: [
      "About 1h of travel via the A8 depending on traffic.",
      "Perfect for the Monaco Grand Prix, galas and dinners in the Principality.",
      "Hourly hire on site available instead of a one-way trip.",
      "Late-night return handled with complete peace of mind.",
    ],
    faq: [
      {
        q: "What is the rate for a Cannes – Monaco transfer?",
        a: "A flat fee per vehicle category, shown above and guaranteed at booking. Tolls and meet-and-greet included, no surprise surcharge.",
      },
      {
        q: "Do you offer hourly hire on site in Monaco?",
        a: "Yes. Beyond the simple transfer, we offer hourly hire for your appointments and evenings in the Principality.",
      },
      {
        q: "Is a late return from Monaco possible?",
        a: "Yes, subject to availability, including after a gala or event. Book the return ahead to secure the chauffeur.",
      },
    ],
    seo: {
      title:
        "Cannes ⇄ Monaco Transfer — Private Chauffeur (VTC) | KS Rent Services",
      description:
        "Private Cannes – Monaco transfer with chauffeur and flat rate. About 1h, fixed price per vehicle, hourly hire available. Book online.",
      canonical: "/en/chauffeur/transfers/cannes-monaco",
    },
  },
  "cannes-saint-tropez": {
    lead: "Private transfer between Cannes and Saint-Tropez with a chauffeur. Reach the gulf in comfort from the Croisette, on an roughly 1h20 ride at a flat rate.",
    highlights: [
      "About 1h20 to 2h depending on summer traffic toward the Gulf of Saint-Tropez.",
      "Class V recommended for groups and luggage.",
      "Ideal for beach stays, yachting and events in Saint-Tropez.",
      "Lunch or shopping stop along the way available.",
    ],
    faq: [
      {
        q: "How much is a Cannes – Saint-Tropez transfer?",
        a: "The flat fee per vehicle is shown above, all-inclusive (tolls, meet-and-greet). The price is fixed and guaranteed from booking.",
      },
      {
        q: "How long is the Cannes – Saint-Tropez trip?",
        a: "About 1h20 off-season and up to 2h on summer weekends. We set the departure time to limit waiting in the gulf.",
      },
      {
        q: "Which vehicle to reach Saint-Tropez?",
        a: "The Class V (premium van) is ideal for comfort, groups and luggage. Sedans suit 2 to 3 light travellers.",
      },
    ],
    seo: {
      title:
        "Cannes ⇄ Saint-Tropez Transfer — Private Chauffeur (VTC) | KS Rent Services",
      description:
        "Private Cannes – Saint-Tropez transfer with chauffeur. About 1h20, all-inclusive flat rate per vehicle. Gulf of Saint-Tropez link. Book now.",
      canonical: "/en/chauffeur/transfers/cannes-saint-tropez",
    },
  },
};
